/**
 * A user-authored evidence constitution compiled without a model call.
 *
 * Profiles may make Pathways stricter. They cannot lower the repository's
 * citation or refutation floors: those are safety invariants, not preferences.
 * The compiler consumes explicit evidence units and emits named diagnostics plus
 * the inputs behind every earned status, so an export can be checked elsewhere.
 */

import type { EvidenceOrigin } from './genealogy'
import type { RecordedSearchObservation } from './coverage'
import { STATUS_WORD } from './vocabulary'

export const MANUAL_BINDABLE_EVIDENCE_ORIGINS = [
  'dataset',
  'trial',
  'cohort',
  'experimental-run',
  'outcome',
] as const satisfies readonly EvidenceOrigin[]

export type ManualBindableEvidenceOrigin = (typeof MANUAL_BINDABLE_EVIDENCE_ORIGINS)[number]
export type ManualEvidenceContentScope = 'full-text' | 'abstract-only' | 'metadata-only'
export type EvidenceAdmissionBasis = 'returned-citation' | 'manual-artifact' | 'rejected-citation'

export type EvidenceSourceClass =
  | 'peer-reviewed-primary'
  | 'preprint'
  | 'systematic-review'
  | 'registry'
  | 'dataset'
  | 'government'
  | 'policy'
  | 'standards'
  | 'news'
  | 'web-primary'
  | 'web-secondary'
  | 'internal-document'
  | 'recorded-outcome'
  | 'unknown'

export type EvidenceReach =
  'full-text' | 'verified-quote' | 'abstract-only' | 'snippet' | 'metadata-only' | 'unreadable'

export type StudyDesign =
  | 'randomized-trial'
  | 'controlled-experiment'
  | 'quasi-experiment'
  | 'cohort'
  | 'case-control'
  | 'cross-sectional'
  | 'case-report'
  | 'systematic-review'
  | 'meta-analysis'
  | 'registry-analysis'
  | 'simulation'
  | 'qualitative'
  | 'official-record'
  | 'unknown'

export type ClaimType =
  | 'causal'
  | 'comparative'
  | 'descriptive'
  | 'mechanistic'
  | 'forecast'
  | 'safety'
  | 'novelty'
  | 'general'

export type EvidenceStance = 'supports' | 'disputes' | 'context'

export interface EvidenceRuleProfile {
  id: string
  name: string
  version: number
  updatedAt: number
  admissibleSourceClasses: EvidenceSourceClass[]
  support: {
    minIndependentUnits: number
    minimumReach: EvidenceReach
  }
  refutation: {
    minIndependentUnits: number
    minIndependentUnitsWithoutVerifiedText: number
    minimumReach: EvidenceReach
  }
  studyDesigns: Partial<Record<ClaimType, StudyDesign[]>>
  promotion: {
    requireSourceCheck: boolean
    requireNoTaint: boolean
    requireNoStaleness: boolean
    maxCheckAgeDays: number | null
    requirePopulationMatch: boolean
    requireTimeframeMatch: boolean
  }
  search: {
    requiredQueryFamilies: string[]
    requiredRetrievalSources: string[]
    /** Missing identifies the frozen legacy compiler; never infer a new policy on reload. */
    policy?: 'recorded-search-v1'
    /** Historical clause only. Supported profiles explicitly retire it with null. */
    minimumEstimatedCoverage: number | null
    allowPartialForSupport: boolean
    absenceRequiresNoFailures: boolean
  }
  budget: {
    maxMoves: number | null
    maxEstimatedUsd: number | null
    maxElapsedMinutes: number | null
  }
  updates: {
    recheckAfterDays: number | null
    recheckOnCorrection: boolean
    recheckOnRetraction: boolean
  }
  stopping: {
    minSupported: number | null
    minRefuted: number | null
    stopOnDecisionCriterion: boolean
  }
}

export const CORE_REFUTATION_MIN = 2
export const CORE_REFUTATION_UNCHECKED_MIN = 3

export const DEFAULT_EVIDENCE_PROFILE: EvidenceRuleProfile = {
  id: 'pathways-default-v2',
  name: 'Pathways default',
  version: 2,
  updatedAt: 0,
  admissibleSourceClasses: [
    'peer-reviewed-primary',
    'preprint',
    'systematic-review',
    'registry',
    'dataset',
    'government',
    'policy',
    'standards',
    'news',
    'web-primary',
    'web-secondary',
    'internal-document',
    'recorded-outcome',
  ],
  support: { minIndependentUnits: 1, minimumReach: 'snippet' },
  refutation: {
    minIndependentUnits: CORE_REFUTATION_MIN,
    minIndependentUnitsWithoutVerifiedText: CORE_REFUTATION_UNCHECKED_MIN,
    minimumReach: 'metadata-only',
  },
  studyDesigns: {},
  promotion: {
    requireSourceCheck: false,
    requireNoTaint: true,
    requireNoStaleness: true,
    maxCheckAgeDays: null,
    requirePopulationMatch: false,
    requireTimeframeMatch: false,
  },
  search: {
    requiredQueryFamilies: [],
    requiredRetrievalSources: [],
    policy: 'recorded-search-v1',
    minimumEstimatedCoverage: null,
    allowPartialForSupport: true,
    absenceRequiresNoFailures: true,
  },
  budget: { maxMoves: null, maxEstimatedUsd: null, maxElapsedMinutes: null },
  updates: { recheckAfterDays: null, recheckOnCorrection: true, recheckOnRetraction: true },
  stopping: { minSupported: null, minRefuted: null, stopOnDecisionCriterion: false },
}

export interface CompilableEvidenceUnit {
  id: string
  familyId: string
  recordId: string | null
  sourceClass: EvidenceSourceClass
  reach: EvidenceReach
  /** Independent predicates: neither full text nor quote verification implies the other. */
  fullTextAvailable: boolean
  verifiedQuote: boolean
  stance: EvidenceStance
  studyDesign: StudyDesign
  populationMatch: boolean | null
  timeframeMatch: boolean | null
  /** Which fail-closed admission rule this unit must satisfy. */
  admissionBasis: EvidenceAdmissionBasis
  /** Null for a citation candidate that has no durable genealogy record. */
  recordOrigin: EvidenceOrigin | null
  /** Present only for a structured manual artefact. */
  contentScope: ManualEvidenceContentScope | null
  citationReturned: boolean
  admitted: boolean
  rejectionReasons: string[]
}

/**
 * The central `refutedIsEarned` gate is evaluated before this compiler boundary.
 * These are the complete fields that gate reads, retained so a receipt can be
 * checked without promoting pass-level page-text capability into source-level
 * verification. The compiler never reimplements the gate.
 */
export interface CoreRefutationGate {
  rule: 'core.refuted-is-earned'
  passed: boolean
  sources: Array<{
    url: string
    stance: EvidenceStance
    via: 'search' | 'fetch'
    quoteFound: boolean | null
    textHash: string | null
    simHash: string | null
  }>
}

export type DisplayedEvidenceStatus =
  | 'supported'
  | 'refuted'
  | 'mixed'
  | 'unchecked'
  | 'nothing-found'
  | 'observed-supported'
  | 'observed-refuted'
  | 'observed-inconclusive'

export interface CompilableClaim {
  nodeId: string
  claim: string
  claimType: ClaimType
  displayedStatus: DisplayedEvidenceStatus
  evidence: CompilableEvidenceUnit[]
  checkedAt: number | null
  now: number
  partialSearch: boolean
  searchFailures: string[]
  queryFamilies: string[]
  retrievalSources: string[]
  estimatedCoverage: { lower: number; upper: number } | null
  /** Absent in historical claim inputs; absence never manufactures a completed search. */
  searchObservation?: RecordedSearchObservation | null
  taintedBy: string[]
  staleBecause: string[]
  modelProvenanceChanged: boolean
  duplicateEvidenceCount: number
  /** A necessary floor for an evidence-earned refutation, never a profile preference. */
  coreRefutation: CoreRefutationGate
  recordedOutcome?: 'supported' | 'refuted' | 'inconclusive'
}

export type DiagnosticSeverity = 'error' | 'warning' | 'note'

export type EpistemicDiagnosticCode =
  | 'citation-not-retrieved'
  | 'duplicate-evidence'
  | 'refuted-premise'
  | 'stale-check'
  | 'population-mismatch'
  | 'timeframe-mismatch'
  | 'model-provenance-changed'
  | 'unearned-label'
  | 'status-understates-earned'
  | 'inadmissible-source'
  | 'insufficient-independent-evidence'
  | 'insufficient-reach'
  | 'study-design-mismatch'
  | 'partial-search'
  | 'search-failure'
  | 'missing-query-family'
  | 'missing-retrieval-source'
  | 'coverage-below-profile'
  | 'coverage-unavailable'
  | 'retired-coverage-policy'
  | 'recorded-search-gap'
  | 'update-overdue'
  | 'correction-recheck-required'
  | 'retraction-recheck-required'
  | 'budget-exceeded'
  | 'budget-not-evaluable'

export interface EpistemicDiagnostic {
  code: EpistemicDiagnosticCode
  severity: DiagnosticSeverity
  nodeId: string
  message: string
  rule: string
  inputs: Record<string, unknown>
  evidenceIds: string[]
}

export interface RuleReceipt {
  rule: string
  passed: boolean
  inputs: Record<string, unknown>
  evidenceIds: string[]
}

export interface ClaimCompilation {
  nodeId: string
  displayedStatus: DisplayedEvidenceStatus
  earnedStatus: DisplayedEvidenceStatus
  promoted: boolean
  receipts: RuleReceipt[]
  diagnostics: EpistemicDiagnostic[]
}

export interface EpistemicCompilation {
  profile: EvidenceRuleProfile
  compiledAt: number
  claims: ClaimCompilation[]
  /** Run-wide rules are separate from claim receipts: they never earn a claim label. */
  policyReceipts: PolicyRuleReceipt[]
  budgetWithinLimits: boolean
  stoppingTriggered: boolean
  diagnostics: EpistemicDiagnostic[]
}

export type PolicyRuleOutcome = 'disabled' | 'met' | 'not-met' | 'not-evaluable'

/** A deterministic evaluation of one budget, update, or stopping clause. */
export interface PolicyRuleReceipt {
  rule: string
  category: 'budget' | 'update' | 'stopping'
  outcome: PolicyRuleOutcome
  inputs: Record<string, unknown>
  nodeId?: string
}

/**
 * Mechanical run facts supplied by the host. Missing facts never become a pass:
 * the corresponding receipt says `not-evaluable`.
 */
export interface EpistemicCompileContext {
  moves?: number
  estimatedUsd?: number
  estimateComplete?: boolean
  elapsedMinutes?: number
  decisionCriterionMet?: boolean
  correctionSourcesByNode?: Readonly<Record<string, readonly string[]>>
  retractionSourcesByNode?: Readonly<Record<string, readonly string[]>>
}

const positiveInt = (value: number, floor: number): number =>
  Math.max(floor, Number.isFinite(value) ? Math.trunc(value) : floor)

const fraction = (value: number): number =>
  Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 0

const optionalPositive = (value: number | null): number | null =>
  value === null || !Number.isFinite(value) ? null : Math.max(0, value)

const unique = <T>(rows: readonly T[]): T[] => [...new Set(rows)]

/** Clamp and dedupe user input while preserving stricter-than-core rules. */
export function normalizeEvidenceProfile(profile: EvidenceRuleProfile): EvidenceRuleProfile {
  const verifiedRefutationMin = positiveInt(
    profile.refutation.minIndependentUnits,
    CORE_REFUTATION_MIN,
  )
  return {
    ...profile,
    id: profile.id.trim().slice(0, 120) || DEFAULT_EVIDENCE_PROFILE.id,
    name: profile.name.trim().slice(0, 120) || 'Untitled evidence profile',
    version: positiveInt(profile.version, 1),
    updatedAt: Math.max(0, Number.isFinite(profile.updatedAt) ? profile.updatedAt : 0),
    admissibleSourceClasses: unique(profile.admissibleSourceClasses).filter(
      (row) => row !== 'unknown',
    ),
    support: {
      ...profile.support,
      minIndependentUnits: positiveInt(profile.support.minIndependentUnits, 1),
    },
    refutation: {
      ...profile.refutation,
      minIndependentUnits: verifiedRefutationMin,
      minIndependentUnitsWithoutVerifiedText: positiveInt(
        Math.max(profile.refutation.minIndependentUnitsWithoutVerifiedText, verifiedRefutationMin),
        CORE_REFUTATION_UNCHECKED_MIN,
      ),
    },
    studyDesigns: Object.fromEntries(
      Object.entries(profile.studyDesigns).map(([kind, designs]) => [kind, unique(designs ?? [])]),
    ),
    promotion: {
      ...profile.promotion,
      maxCheckAgeDays: optionalPositive(profile.promotion.maxCheckAgeDays),
    },
    search: {
      ...profile.search,
      requiredQueryFamilies: unique(
        profile.search.requiredQueryFamilies.map((row) => row.trim()),
      ).filter(Boolean),
      requiredRetrievalSources: unique(
        profile.search.requiredRetrievalSources.map((row) => row.trim()),
      ).filter(Boolean),
      minimumEstimatedCoverage:
        profile.search.minimumEstimatedCoverage === null
          ? null
          : fraction(profile.search.minimumEstimatedCoverage),
    },
    budget: {
      maxMoves: profile.budget.maxMoves === null ? null : positiveInt(profile.budget.maxMoves, 1),
      maxEstimatedUsd: optionalPositive(profile.budget.maxEstimatedUsd),
      maxElapsedMinutes: optionalPositive(profile.budget.maxElapsedMinutes),
    },
    updates: {
      ...profile.updates,
      recheckAfterDays: optionalPositive(profile.updates.recheckAfterDays),
    },
    stopping: {
      ...profile.stopping,
      minSupported:
        profile.stopping.minSupported === null
          ? null
          : positiveInt(profile.stopping.minSupported, 1),
      minRefuted:
        profile.stopping.minRefuted === null ? null : positiveInt(profile.stopping.minRefuted, 1),
    },
  }
}

/** A new run must never silently reinterpret a frozen percentage policy. */
export function evidenceProfileStartIssue(profile: EvidenceRuleProfile): string | null {
  return profile.search.policy !== 'recorded-search-v1' ||
    profile.search.minimumEstimatedCoverage !== null
    ? 'This profile uses a retired numerical coverage policy. In Research Lab → Protocol, choose “Use recorded search work” and save the revised profile before starting a new run. The revision preserves your budgets and evidence rules; a completed search can describe only what was found in its recorded scope, never establish absence or novelty.'
    : null
}

/** An explicit user choice creates a revision; loading an old profile never calls this. */
export function recordedSearchProfile(
  profile: EvidenceRuleProfile,
  updatedAt: number,
): EvidenceRuleProfile {
  return normalizeEvidenceProfile({
    ...profile,
    version: profile.version + 1,
    updatedAt,
    search: {
      ...profile.search,
      policy: 'recorded-search-v1',
      minimumEstimatedCoverage: null,
      absenceRequiresNoFailures: true,
    },
  })
}

/**
 * Full text and a verified quote are orthogonal requirements. The remaining
 * transport reaches form a conservative containment ladder.
 */
const reaches = (unit: CompilableEvidenceUnit, required: EvidenceReach): boolean => {
  if (required === 'full-text') return unit.fullTextAvailable
  if (required === 'verified-quote') return unit.verifiedQuote
  if (required === 'unreadable') return true
  if (required === 'metadata-only') return unit.reach !== 'unreadable'
  if (required === 'snippet') {
    return (
      unit.verifiedQuote ||
      unit.fullTextAvailable ||
      unit.reach === 'snippet' ||
      unit.reach === 'abstract-only'
    )
  }
  return unit.fullTextAvailable || unit.reach === 'abstract-only'
}

const diagnostic = (
  claim: CompilableClaim,
  code: EpistemicDiagnosticCode,
  severity: DiagnosticSeverity,
  message: string,
  rule: string,
  inputs: Record<string, unknown>,
  evidenceIds: string[] = [],
): EpistemicDiagnostic => ({
  code,
  severity,
  nodeId: claim.nodeId,
  message,
  rule,
  inputs,
  evidenceIds,
})

const families = (rows: readonly CompilableEvidenceUnit[]): number =>
  new Set(rows.map((row) => row.familyId)).size

const designsFor = (profile: EvidenceRuleProfile, type: ClaimType): StudyDesign[] =>
  profile.studyDesigns[type] ?? []

function admissionRule(unit: CompilableEvidenceUnit): string {
  if (unit.admissionBasis === 'manual-artifact') return 'evidence.admission.manual-artifact'
  if (unit.admissionBasis === 'rejected-citation') return 'evidence.admission.rejected-citation'
  return 'evidence.admission.returned-citation'
}

function admissionPassed(unit: CompilableEvidenceUnit): boolean {
  if (!unit.admitted) return false
  if (unit.admissionBasis === 'returned-citation') return unit.citationReturned
  if (unit.admissionBasis === 'rejected-citation') return false
  return (
    !unit.citationReturned &&
    unit.recordOrigin !== null &&
    MANUAL_BINDABLE_EVIDENCE_ORIGINS.includes(unit.recordOrigin as ManualBindableEvidenceOrigin)
  )
}

/** Compile one claim and carry every decisive input in a receipt. */
export function compileClaim(
  rawProfile: EvidenceRuleProfile,
  claim: CompilableClaim,
  context: EpistemicCompileContext = {},
): ClaimCompilation {
  const profile = normalizeEvidenceProfile(rawProfile)
  const diagnostics: EpistemicDiagnostic[] = []
  const receipts: RuleReceipt[] = []
  const admissibleClasses = new Set(profile.admissibleSourceClasses)
  const allowedDesigns = designsFor(profile, claim.claimType)

  for (const unit of claim.evidence) {
    const admissionPassedForUnit = admissionPassed(unit)
    receipts.push({
      rule: admissionRule(unit),
      passed: admissionPassedForUnit,
      inputs:
        unit.admissionBasis === 'manual-artifact'
          ? {
              bindingId: unit.id,
              recordId: unit.recordId,
              recordOrigin: unit.recordOrigin,
              contentScope: unit.contentScope,
              citationReturned: unit.citationReturned,
            }
          : {
              admissionBasis: unit.admissionBasis,
              citationReturned: unit.citationReturned,
              admitted: unit.admitted,
              rejectionReasons: unit.rejectionReasons,
            },
      evidenceIds: [unit.id],
    })
    if (!unit.citationReturned && unit.admissionBasis !== 'manual-artifact') {
      diagnostics.push(
        diagnostic(
          claim,
          'citation-not-retrieved',
          'error',
          'A citation never appeared in the retrieval record.',
          'citation.must-appear-in-retrieval',
          { evidenceId: unit.id },
          [unit.id],
        ),
      )
    }
    // Rejected candidates remain compiler inputs so the citation gate is
    // inspectable, but they cannot fail source-class, scope, reach, or design
    // rules as though they had been admitted evidence.
    if (!unit.admitted) continue
    if (!admissibleClasses.has(unit.sourceClass)) {
      diagnostics.push(
        diagnostic(
          claim,
          'inadmissible-source',
          'error',
          `${unit.sourceClass} is not admissible under this profile.`,
          'source.class.admissible',
          { sourceClass: unit.sourceClass },
          [unit.id],
        ),
      )
    }
    if (profile.promotion.requirePopulationMatch && unit.populationMatch !== true) {
      diagnostics.push(
        diagnostic(
          claim,
          'population-mismatch',
          'error',
          unit.populationMatch === false
            ? 'Evidence falls outside the claim population.'
            : 'The evidence population has not been matched to this claim.',
          'scope.population.match',
          { populationMatch: unit.populationMatch },
          [unit.id],
        ),
      )
    }
    if (profile.promotion.requireTimeframeMatch && unit.timeframeMatch !== true) {
      diagnostics.push(
        diagnostic(
          claim,
          'timeframe-mismatch',
          'error',
          unit.timeframeMatch === false
            ? 'Evidence falls outside the claim timeframe.'
            : 'The evidence timeframe has not been matched to this claim.',
          'scope.timeframe.match',
          { timeframeMatch: unit.timeframeMatch },
          [unit.id],
        ),
      )
    }
    if (allowedDesigns.length && !allowedDesigns.includes(unit.studyDesign)) {
      diagnostics.push(
        diagnostic(
          claim,
          'study-design-mismatch',
          'error',
          `${unit.studyDesign} does not satisfy this claim type.`,
          'study-design.satisfies-claim-type',
          { claimType: claim.claimType, allowedDesigns, actual: unit.studyDesign },
          [unit.id],
        ),
      )
    }
  }

  if (claim.duplicateEvidenceCount > 0) {
    diagnostics.push(
      diagnostic(
        claim,
        'duplicate-evidence',
        'warning',
        `${claim.duplicateEvidenceCount} source record${claim.duplicateEvidenceCount === 1 ? '' : 's'} collapsed into existing evidence families.`,
        'evidence.count-independent-families',
        { duplicateEvidenceCount: claim.duplicateEvidenceCount },
      ),
    )
  }
  if (claim.taintedBy.length) {
    diagnostics.push(
      diagnostic(
        claim,
        'refuted-premise',
        'error',
        // The card's words for the same walk (`taint.ts`), so the Lab's compiler
        // and the claim's status line do not name one state two ways.
        `${STATUS_WORD.standsOnRefuted}, and has not been re-derived since.`,
        'dependency.no-refuted-premise',
        { taintedBy: claim.taintedBy },
      ),
    )
  }
  if (claim.staleBecause.length) {
    diagnostics.push(
      diagnostic(
        claim,
        'stale-check',
        'error',
        'This claim predates a material source-record change.',
        'dependency.current-evidence',
        { staleBecause: claim.staleBecause },
      ),
    )
  }
  if (claim.modelProvenanceChanged) {
    diagnostics.push(
      diagnostic(
        claim,
        'model-provenance-changed',
        'warning',
        'The model provenance changed between relevant evidence records.',
        'provenance.model-stable-or-disclosed',
        { changed: true },
      ),
    )
  }
  if (claim.partialSearch) {
    diagnostics.push(
      diagnostic(
        claim,
        'partial-search',
        claim.displayedStatus === 'nothing-found' || !profile.search.allowPartialForSupport
          ? 'error'
          : 'warning',
        'The retrieval loop ended before the search was exhausted.',
        'search.not-partial',
        { partial: true },
      ),
    )
  }
  if (claim.searchFailures.length) {
    diagnostics.push(
      diagnostic(
        claim,
        'search-failure',
        claim.displayedStatus === 'nothing-found' && profile.search.absenceRequiresNoFailures
          ? 'error'
          : 'warning',
        'At least one planned retrieval step failed.',
        'search.steps-succeeded',
        { failures: claim.searchFailures },
      ),
    )
  }

  const missingFamilies = profile.search.requiredQueryFamilies.filter(
    (row) => !claim.queryFamilies.includes(row),
  )
  if (missingFamilies.length) {
    diagnostics.push(
      diagnostic(
        claim,
        'missing-query-family',
        'error',
        'The profile requires query families this check did not run.',
        'search.query-families.complete',
        { missing: missingFamilies },
      ),
    )
  }
  const missingSources = profile.search.requiredRetrievalSources.filter(
    (row) => !claim.retrievalSources.includes(row),
  )
  if (missingSources.length) {
    diagnostics.push(
      diagnostic(
        claim,
        'missing-retrieval-source',
        'error',
        'The profile requires retrieval sources this check did not reach.',
        'search.retrieval-sources.complete',
        { missing: missingSources },
      ),
    )
  }
  if (
    profile.search.policy !== 'recorded-search-v1' &&
    profile.search.minimumEstimatedCoverage !== null &&
    claim.estimatedCoverage &&
    claim.estimatedCoverage.lower < profile.search.minimumEstimatedCoverage
  ) {
    diagnostics.push(
      diagnostic(
        claim,
        'coverage-below-profile',
        'error',
        'The conservative coverage estimate is below the profile floor.',
        'search.coverage-lower-bound',
        {
          lower: claim.estimatedCoverage.lower,
          required: profile.search.minimumEstimatedCoverage,
        },
      ),
    )
  }
  if (
    profile.search.policy !== 'recorded-search-v1' &&
    !claim.estimatedCoverage &&
    profile.search.minimumEstimatedCoverage !== null &&
    profile.search.minimumEstimatedCoverage > 0
  ) {
    diagnostics.push(
      diagnostic(
        claim,
        'coverage-unavailable',
        'error',
        'This check has no estimated coverage interval to compare with the profile floor.',
        'search.coverage-lower-bound',
        { lower: null, required: profile.search.minimumEstimatedCoverage },
      ),
    )
  }

  const usable = claim.evidence.filter(
    (unit) =>
      admissionPassed(unit) &&
      admissibleClasses.has(unit.sourceClass) &&
      (!allowedDesigns.length || allowedDesigns.includes(unit.studyDesign)) &&
      (!profile.promotion.requirePopulationMatch || unit.populationMatch === true) &&
      (!profile.promotion.requireTimeframeMatch || unit.timeframeMatch === true),
  )
  const supporting = usable.filter(
    (unit) => unit.stance === 'supports' && reaches(unit, profile.support.minimumReach),
  )
  const disputing = usable.filter(
    (unit) => unit.stance === 'disputes' && reaches(unit, profile.refutation.minimumReach),
  )
  const supportUnits = families(supporting)
  const disputeUnits = families(disputing)
  const allDisputesVerified = disputing.every((unit) => unit.verifiedQuote)
  const refutationMin = allDisputesVerified
    ? profile.refutation.minIndependentUnits
    : profile.refutation.minIndependentUnitsWithoutVerifiedText
  const refutationEarned = disputeUnits >= refutationMin && claim.coreRefutation.passed

  receipts.push({
    rule: 'support.independent-units',
    passed: supportUnits >= profile.support.minIndependentUnits,
    inputs: { actual: supportUnits, required: profile.support.minIndependentUnits },
    evidenceIds: supporting.map((unit) => unit.id),
  })
  receipts.push({
    rule: 'refutation.independent-units',
    passed: disputeUnits >= refutationMin,
    inputs: { actual: disputeUnits, required: refutationMin, allDisputesVerified },
    evidenceIds: disputing.map((unit) => unit.id),
  })
  receipts.push({
    rule: claim.coreRefutation.rule,
    passed: claim.coreRefutation.passed,
    inputs: { sources: claim.coreRefutation.sources },
    evidenceIds: disputing.map((unit) => unit.id),
  })

  const recordedSearch = profile.search.policy === 'recorded-search-v1'
  let absenceSearchSufficient: boolean
  if (recordedSearch) {
    const observation = claim.searchObservation
    const supportedPolicy = profile.search.minimumEstimatedCoverage === null
    // This predicate permits bounded reporting, never evidence of absence or novelty.
    absenceSearchSufficient =
      supportedPolicy &&
      claim.checkedAt !== null &&
      !claim.partialSearch &&
      claim.searchFailures.length === 0 &&
      missingFamilies.length === 0 &&
      missingSources.length === 0 &&
      observation?.kind === 'recorded-search-v1' &&
      observation.completedRounds > 0 &&
      observation.gaps.length === 0
    receipts.push({
      rule: 'search.recorded-work.v1',
      passed: absenceSearchSufficient,
      inputs: {
        checked: claim.checkedAt !== null,
        partial: claim.partialSearch,
        failures: claim.searchFailures,
        missingQueryFamilies: missingFamilies,
        missingRetrievalSources: missingSources,
        observation: observation ?? null,
        permittedWording: 'Nothing found in the recorded search.',
        supportsAbsence: false,
        supportsNovelty: false,
      },
      evidenceIds: [],
    })
    if (!supportedPolicy) {
      diagnostics.push(
        diagnostic(
          claim,
          'retired-coverage-policy',
          'error',
          'Numerical coverage clauses are retired. Explicitly choose the recorded-search profile before using this policy.',
          'search.recorded-work.v1',
          { minimumEstimatedCoverage: profile.search.minimumEstimatedCoverage },
        ),
      )
    }
    if (claim.displayedStatus === 'nothing-found' && !absenceSearchSufficient) {
      diagnostics.push(
        diagnostic(
          claim,
          'recorded-search-gap',
          'error',
          'The recorded search has missing work, access gaps, or no completed trace. It cannot support even a bounded negative-search statement.',
          'search.recorded-work.v1',
          { observation: observation ?? null },
        ),
      )
    }
  } else {
    absenceSearchSufficient =
      claim.checkedAt !== null &&
      !claim.partialSearch &&
      (!profile.search.absenceRequiresNoFailures || claim.searchFailures.length === 0) &&
      missingFamilies.length === 0 &&
      missingSources.length === 0 &&
      (profile.search.minimumEstimatedCoverage === 0 ||
        (claim.estimatedCoverage !== null &&
          profile.search.minimumEstimatedCoverage !== null &&
          claim.estimatedCoverage.lower >= profile.search.minimumEstimatedCoverage))
    receipts.push({
      rule: 'absence.search-sufficient',
      passed: absenceSearchSufficient,
      inputs: {
        checked: claim.checkedAt !== null,
        partial: claim.partialSearch,
        failures: claim.searchFailures,
        absenceRequiresNoFailures: profile.search.absenceRequiresNoFailures,
        missingQueryFamilies: missingFamilies,
        missingRetrievalSources: missingSources,
        estimatedCoverage: claim.estimatedCoverage,
        minimumEstimatedCoverage: profile.search.minimumEstimatedCoverage,
      },
      evidenceIds: [],
    })
  }

  let earnedStatus: DisplayedEvidenceStatus = 'unchecked'
  if (claim.recordedOutcome) {
    earnedStatus = `observed-${claim.recordedOutcome}` as DisplayedEvidenceStatus
    receipts.push({
      rule: 'outcome.outranks-evidence',
      passed: true,
      inputs: { bearing: claim.recordedOutcome },
      evidenceIds: [],
    })
  } else if (supportUnits >= profile.support.minIndependentUnits && disputeUnits > 0)
    earnedStatus = 'mixed'
  else if (refutationEarned) earnedStatus = 'refuted'
  else if (supportUnits >= profile.support.minIndependentUnits) earnedStatus = 'supported'
  else if (claim.displayedStatus === 'nothing-found' && absenceSearchSufficient)
    earnedStatus = 'nothing-found'

  if (claim.displayedStatus === 'unchecked' && earnedStatus !== 'unchecked') {
    diagnostics.push(
      diagnostic(
        claim,
        'status-understates-earned',
        'note',
        `Displayed unchecked; this profile earns ${earnedStatus}.`,
        'status.may-understate-earned',
        { displayed: claim.displayedStatus, earned: earnedStatus },
      ),
    )
  } else if (claim.displayedStatus !== earnedStatus) {
    diagnostics.push(
      diagnostic(
        claim,
        'unearned-label',
        'error',
        `Displayed ${claim.displayedStatus}; this profile earns ${earnedStatus}.`,
        'status.must-be-earned',
        { displayed: claim.displayedStatus, earned: earnedStatus },
      ),
    )
  }

  const ageDays =
    claim.checkedAt === null ? null : Math.max(0, (claim.now - claim.checkedAt) / 86_400_000)
  const currentEnough =
    profile.promotion.maxCheckAgeDays === null ||
    (ageDays !== null && ageDays <= profile.promotion.maxCheckAgeDays)
  if (!currentEnough) {
    diagnostics.push(
      diagnostic(
        claim,
        'stale-check',
        'error',
        'The latest source check is older than this profile allows.',
        'promotion.check-age',
        { ageDays, maximum: profile.promotion.maxCheckAgeDays },
      ),
    )
  }

  if (
    profile.updates.recheckAfterDays !== null &&
    (ageDays === null || ageDays > profile.updates.recheckAfterDays)
  ) {
    diagnostics.push(
      diagnostic(
        claim,
        'update-overdue',
        'error',
        ageDays === null
          ? 'This profile requires a dated source check before the claim can be current.'
          : 'This claim has reached the profile’s scheduled recheck interval.',
        'update.recheck-after-days',
        { ageDays, maximum: profile.updates.recheckAfterDays },
      ),
    )
  }

  const correctionSources = [...(context.correctionSourcesByNode?.[claim.nodeId] ?? [])]
  if (profile.updates.recheckOnCorrection && correctionSources.length) {
    diagnostics.push(
      diagnostic(
        claim,
        'correction-recheck-required',
        'error',
        'A corrected source in this claim’s dependency path requires rechecking.',
        'update.recheck-on-correction',
        { sourceIds: correctionSources },
      ),
    )
  }
  const retractionSources = [...(context.retractionSourcesByNode?.[claim.nodeId] ?? [])]
  if (profile.updates.recheckOnRetraction && retractionSources.length) {
    diagnostics.push(
      diagnostic(
        claim,
        'retraction-recheck-required',
        'error',
        'A retracted source in this claim’s dependency path requires rechecking.',
        'update.recheck-on-retraction',
        { sourceIds: retractionSources },
      ),
    )
  }

  const promoted =
    (!recordedSearch || earnedStatus !== 'nothing-found') &&
    (!profile.promotion.requireSourceCheck || claim.checkedAt !== null) &&
    (!profile.promotion.requireNoTaint || claim.taintedBy.length === 0) &&
    (!profile.promotion.requireNoStaleness || claim.staleBecause.length === 0) &&
    currentEnough &&
    !diagnostics.some((row) => row.severity === 'error')

  receipts.push({
    rule: 'promotion.profile-prerequisites',
    passed: promoted,
    inputs: {
      requireSourceCheck: profile.promotion.requireSourceCheck,
      checked: claim.checkedAt !== null,
      tainted: claim.taintedBy.length > 0,
      stale: claim.staleBecause.length > 0,
      currentEnough,
      updateCurrent: !diagnostics.some((row) =>
        ['update-overdue', 'correction-recheck-required', 'retraction-recheck-required'].includes(
          row.code,
        ),
      ),
    },
    evidenceIds: usable.map((unit) => unit.id),
  })

  return {
    nodeId: claim.nodeId,
    displayedStatus: claim.displayedStatus,
    earnedStatus,
    promoted,
    receipts,
    diagnostics,
  }
}

export function compileEpistemics(
  profile: EvidenceRuleProfile,
  claims: readonly CompilableClaim[],
  compiledAt = Date.now(),
  context: EpistemicCompileContext = {},
): EpistemicCompilation {
  const normalized = normalizeEvidenceProfile(profile)
  const compiled = claims.map((claim) => compileClaim(normalized, claim, context))
  const policyReceipts: PolicyRuleReceipt[] = []
  const policyDiagnostics: EpistemicDiagnostic[] = []

  const budgetRule = (
    rule: string,
    actual: number | undefined,
    maximum: number | null,
    extraInputs: Record<string, unknown> = {},
  ): void => {
    if (maximum === null) {
      policyReceipts.push({
        rule,
        category: 'budget',
        outcome: 'disabled',
        inputs: { maximum, ...extraInputs },
      })
      return
    }
    if (actual === undefined || !Number.isFinite(actual)) {
      policyReceipts.push({
        rule,
        category: 'budget',
        outcome: 'not-evaluable',
        inputs: { actual: actual ?? null, maximum, ...extraInputs },
      })
      policyDiagnostics.push({
        code: 'budget-not-evaluable',
        severity: 'warning',
        nodeId: '$run',
        message: 'A configured budget cannot be evaluated from the recorded run facts.',
        rule,
        inputs: { actual: actual ?? null, maximum, ...extraInputs },
        evidenceIds: [],
      })
      return
    }
    const met = actual <= maximum
    policyReceipts.push({
      rule,
      category: 'budget',
      outcome: met ? 'met' : 'not-met',
      inputs: { actual, maximum, ...extraInputs },
    })
    if (!met) {
      policyDiagnostics.push({
        code: 'budget-exceeded',
        severity: 'warning',
        nodeId: '$run',
        message: 'A configured research budget has been exceeded.',
        rule,
        inputs: { actual, maximum, ...extraInputs },
        evidenceIds: [],
      })
    }
  }

  budgetRule('budget.maximum-moves', context.moves, normalized.budget.maxMoves)
  budgetRule(
    'budget.maximum-estimate-usd',
    context.estimateComplete === false ? undefined : context.estimatedUsd,
    normalized.budget.maxEstimatedUsd,
    { estimateComplete: context.estimateComplete ?? null },
  )
  budgetRule(
    'budget.maximum-elapsed-minutes',
    context.elapsedMinutes,
    normalized.budget.maxElapsedMinutes,
  )

  for (const claim of claims) {
    const ageDays =
      claim.checkedAt === null ? null : Math.max(0, (claim.now - claim.checkedAt) / 86_400_000)
    policyReceipts.push({
      rule: 'update.recheck-after-days',
      category: 'update',
      nodeId: claim.nodeId,
      outcome:
        normalized.updates.recheckAfterDays === null
          ? 'disabled'
          : ageDays !== null && ageDays <= normalized.updates.recheckAfterDays
            ? 'met'
            : 'not-met',
      inputs: { ageDays, maximum: normalized.updates.recheckAfterDays },
    })

    const correctionSources = context.correctionSourcesByNode?.[claim.nodeId]
    policyReceipts.push({
      rule: 'update.recheck-on-correction',
      category: 'update',
      nodeId: claim.nodeId,
      outcome: !normalized.updates.recheckOnCorrection
        ? 'disabled'
        : context.correctionSourcesByNode === undefined
          ? 'not-evaluable'
          : correctionSources?.length
            ? 'not-met'
            : 'met',
      inputs: {
        enabled: normalized.updates.recheckOnCorrection,
        sourceIds: correctionSources ?? [],
      },
    })

    const retractionSources = context.retractionSourcesByNode?.[claim.nodeId]
    policyReceipts.push({
      rule: 'update.recheck-on-retraction',
      category: 'update',
      nodeId: claim.nodeId,
      outcome: !normalized.updates.recheckOnRetraction
        ? 'disabled'
        : context.retractionSourcesByNode === undefined
          ? 'not-evaluable'
          : retractionSources?.length
            ? 'not-met'
            : 'met',
      inputs: {
        enabled: normalized.updates.recheckOnRetraction,
        sourceIds: retractionSources ?? [],
      },
    })
  }

  const supported = compiled.filter(
    (claim) => claim.earnedStatus === 'supported' || claim.earnedStatus === 'observed-supported',
  ).length
  const refuted = compiled.filter(
    (claim) => claim.earnedStatus === 'refuted' || claim.earnedStatus === 'observed-refuted',
  ).length
  const stoppingRule = (
    rule: string,
    enabled: boolean,
    met: boolean | undefined,
    inputs: Record<string, unknown>,
  ): void => {
    policyReceipts.push({
      rule,
      category: 'stopping',
      outcome: !enabled
        ? 'disabled'
        : met === undefined
          ? 'not-evaluable'
          : met
            ? 'met'
            : 'not-met',
      inputs,
    })
  }
  stoppingRule(
    'stopping.minimum-supported',
    normalized.stopping.minSupported !== null,
    normalized.stopping.minSupported === null
      ? undefined
      : supported >= normalized.stopping.minSupported,
    { actual: supported, required: normalized.stopping.minSupported },
  )
  stoppingRule(
    'stopping.minimum-refuted',
    normalized.stopping.minRefuted !== null,
    normalized.stopping.minRefuted === null ? undefined : refuted >= normalized.stopping.minRefuted,
    { actual: refuted, required: normalized.stopping.minRefuted },
  )
  stoppingRule(
    'stopping.decision-criterion',
    normalized.stopping.stopOnDecisionCriterion,
    context.decisionCriterionMet,
    { met: context.decisionCriterionMet ?? null },
  )

  const budgetReceipts = policyReceipts.filter((receipt) => receipt.category === 'budget')
  const stoppingReceipts = policyReceipts.filter((receipt) => receipt.category === 'stopping')
  return {
    profile: normalized,
    compiledAt,
    claims: compiled,
    policyReceipts,
    budgetWithinLimits: budgetReceipts.every(
      (receipt) => receipt.outcome === 'disabled' || receipt.outcome === 'met',
    ),
    stoppingTriggered: stoppingReceipts.some((receipt) => receipt.outcome === 'met'),
    diagnostics: [...compiled.flatMap((claim) => claim.diagnostics), ...policyDiagnostics],
  }
}
